import type { RatingBreakdown, RatingPeriodBreakdown } from '@brush/api-client';
import { describeApiError } from '@brush/api-client';
import {
  Pill,
  PointsBreakdownEventRow,
  Spinner,
  decayedFigure,
  periodCutLabel,
  periodRangeLabel,
  periodWeightLabel,
  ratingPeriodView,
  scoredNothingNote,
  tailDroppedNote,
} from '@brush/ui';
import { useRatingBreakdown } from '../../queries';

/**
 * §7's breakdown, opened under a row of §9.5's standings — why this player has the rating they have.
 *
 * <b>A rating adds two reasons a number is not what somebody expects, and this panel exists for
 * both.</b> A leaderboard's breakdown answers "what was each finish worth"; a rating has to answer
 * that and then "which of my results counted" and "what did age do to them". So the shape is
 * period-first: each period states its own dates, its cut and its weight, and the results inside it
 * are listed best raw score first — §3.3's own selection order, so the cut line is visible where it
 * falls.
 *
 * <b>Nothing here is computed.</b> Every figure is the server's, frozen into the snapshot the
 * standings above are reading. The nested per-event working is the points engine's own object
 * rendered by the points designer's own component (`PointsBreakdownEventRow`), verbatim — §7 forbids
 * re-deriving, reshaping or summarising it, and the fantasy F6 defect was a label reading the wrong
 * field.
 *
 * <b>What it deliberately does not render is `counted`.</b> Through `ScoreEvents` it is `true` on
 * every row, so showing it would put "counted" beside a dropped result on every dropped row. §7 says
 * the field is carried in the data and not surfaced, and `selected` is the rating's authoritative
 * answer to the question a reader would take it to be answering.
 */
export function RatingBreakdownPanel({
  boardId,
  sequenceNumber,
  playerId,
}: {
  boardId: string;

  /**
   * The snapshot the standings above came from — <b>part of the cache key, which is what makes
   * "fetched once per player" structural rather than a window.</b> A recalculation produces a new
   * sequence number and therefore a new key, so the stored answer can be held forever without ever
   * going stale: a frozen snapshot's breakdown is frozen too.
   */
  sequenceNumber: number;

  playerId: string;
}) {
  const query = useRatingBreakdown(boardId, sequenceNumber, playerId);

  if (query.isLoading) return <Spinner label="Working out the rating…" />;

  if (query.isError || !query.data) {
    return (
      <p role="alert" className="px-5 py-4 text-[11.5px] text-ink-800/60">
        {describeApiError(query.error) ?? 'Couldn’t load this rating’s working.'} Nothing about the
        standings above has changed — this is the working that couldn’t be fetched.
      </p>
    );
  }

  const breakdown = query.data;
  const places = breakdown.decimalPlaces;

  if (breakdown.periods.length === 0) {
    return (
      <p className="border-l-2 border-line bg-felt-50/40 px-5 py-4 text-[11.5px] text-ink-800/60">
        {/*
         * Three absences, three sentences, because each sends an operator somewhere different:
         * recalculate the board, check which events are in scope, or look at the cohort. A single
         * "nothing to show" would send them to none of the three.
         */}
        {breakdown.sequenceNumber === null
          ? 'This rating has not been worked out yet, so there is nothing frozen to break down.'
          : breakdown.excludedFromCohort
            ? 'This player is outside this board’s cohort, so the rating never scored them. Check the cohort rules if that is not what you expect.'
            : 'None of the events this rating scores has a result for this player.'}
      </p>
    );
  }

  const tail = tailDroppedNote(breakdown.tailDropped);

  return (
    <div className="space-y-3 border-l-2 border-line bg-felt-50/40 px-5 py-4">
      <header className="space-y-1">
        <p className="text-[11.5px] leading-relaxed text-ink-800/70">
          <span className="num font-semibold text-ink-950">{breakdown.displayRating}</span> in
          total — <span className="num">{breakdown.eventsSelected}</span> of{' '}
          <span className="num">{breakdown.eventsEntered}</span>{' '}
          {breakdown.eventsEntered === 1 ? 'event' : 'events'} counted, over{' '}
          {breakdown.periods.length} {breakdown.periods.length === 1 ? 'period' : 'periods'}.
          {breakdown.isQualified ? '' : ' Provisional'}
          {/*
           * The server's sentence, verbatim, for the reason the engine's explanations are: two
           * consoles and a portal wording a shortfall independently are three implementations of one
           * rule that nothing tests.
           */}
          {breakdown.isQualified || breakdown.qualificationShortfall === null
            ? ''
            : ` — ${breakdown.qualificationShortfall}`}
          {breakdown.isQualified ? '' : '.'}
        </p>

        {/*
         * §2.2's anchor, stated on the panel as well as on the table above it. The panel can be
         * scrolled to with the header off screen, and every date below is measured back from this
         * one — a period's bounds are uncheckable without it.
         */}
        <p className="num text-[11px] text-ink-800/55">
          As of {breakdown.asOfDate}
          {snapshotNote(breakdown)}
        </p>
      </header>

      {tail === null ? null : <p className="text-[11px] text-ink-800/60">{tail}</p>}

      <ol className="space-y-3">
        {breakdown.periods.map((period) => (
          <Period key={period.index} period={period} places={places} />
        ))}
      </ol>
    </div>
  );
}

/** "· snapshot 4" where there is one. Split out so the anchor line stays one expression. */
function snapshotNote(breakdown: RatingBreakdown): string {
  if (breakdown.sequenceNumber === null) return '';
  return ` · snapshot ${breakdown.sequenceNumber}${breakdown.isFinal ? ', final' : ''}`;
}

/**
 * One period: its own dates, its cut, its weight, and what bucketed into it.
 *
 * <b>The dates are the whole reason this is grouped by period rather than listed flat.</b> §7 asks
 * for them so a reader can check §3.2's bucketing without knowing the anchor arithmetic — and the
 * `from` the server sends is already the first <i>included</i> date, not §3.2's exclusive lower edge.
 */
function Period({ period, places }: { period: RatingPeriodBreakdown; places: number }) {
  const view = ratingPeriodView(period);
  const collapsed = scoredNothingNote(view.scoredNothing);

  return (
    <li>
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 border-b border-line/60 pb-1 text-[11px] text-ink-800/60">
        <span className="font-semibold text-ink-950">Period {period.index}</span>
        <span className="num">{periodRangeLabel(period.from, period.to)}</span>
        <span>·</span>
        <span>{periodCutLabel(period.n)}</span>
        <span>·</span>
        <span>{periodWeightLabel(period.decay)}</span>
        <span className="num ml-auto font-semibold text-ink-950">
          {period.contribution.toFixed(places)}
        </span>
      </div>

      {view.rows.length === 0 ? null : (
        <ol className="mt-1 space-y-2">
          {view.rows.map((result) => (
            <PointsBreakdownEventRow
              key={result.eventId}
              event={result.breakdown}
              places={places}
              // §3.3's answer, not §3.9's. `counted` is true on every one of these rows.
              dimmed={!result.selected}
            >
              <RatingRow result={result} places={places} />
            </PointsBreakdownEventRow>
          ))}
        </ol>
      )}

      {collapsed === null ? null : (
        <p className="mt-1.5 text-[11px] text-ink-800/55">{collapsed}</p>
      )}
    </li>
  );
}

/**
 * What the rating layer adds to the engine's own working: what this result actually contributed, and
 * why, when the two differ.
 *
 * <b>§7 puts the decayed score ON THE ROW, and this is that.</b> "Why did this contribute 45 instead
 * of 50" is answered where it is asked. `decayedFigure` withholds it where nothing happened — a row
 * whose decayed score is its raw score has neither aged nor been cut, and `50 → 50` spends attention
 * on the case that needs none.
 */
function RatingRow({
  result,
  places,
}: {
  result: RatingPeriodBreakdown['results'][number];
  places: number;
}) {
  const decayed = decayedFigure(result);

  return (
    <div className="mt-1 flex flex-wrap items-baseline gap-x-2 gap-y-1 border-t border-line/40 pt-1 text-[11px]">
      {result.selected ? (
        <Pill tone="good">Counts</Pill>
      ) : (
        <Pill tone="warn">Did not count</Pill>
      )}

      {decayed === null ? null : (
        <span className="num text-ink-800/70">
          {/* Figures alone, no clause explaining decay: this reader wrote the ramp and is checking
              arithmetic against it. player-web says the same fact at length because a player has
              never seen the ramp — see `decayedFigure` in @brush/ui for why the two must not be
              unified. */}
          contributed {decayed.toFixed(places)} of {result.eventScore.toFixed(places)}
        </span>
      )}

      {/* The server's sentence, verbatim — §3.3 writes two of them and they answer different
          questions: one says the player was beaten, the other that there was nothing to beat. */}
      {result.selectionNote === null ? null : (
        <span className="text-ink-800/70">{result.selectionNote}</span>
      )}
    </div>
  );
}
